import {
  RoutineWorkflow,
  useRoutineAlerts,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  AlertSummary,
  LoadingIndicator,
  SlideAction,
  tokens,
  useSlideLayoutSafe,
} from '@gripp/shared-ui';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAssetRouting } from '../assetRouteContext';

type AlertPanelProps = {
  assetId: string;
  routineId: string;
};

const toRouteStateObject = (state: unknown): Record<string, unknown> =>
  state && typeof state === 'object' && !Array.isArray(state)
    ? (state as Record<string, unknown>)
    : {};

/**
 * AlertPanel renders the alert list inside the SlideLayout panel (showHeader: false).
 * Navigates to separate pages for creating or editing an alert.
 */
export const AlertPanel = ({ assetId, routineId }: AlertPanelProps) => {
  const navigate = useNavigate();
  const slideLayout = useSlideLayoutSafe();
  const location = useLocation();
  const { workspaceAsset: rawWorkspaceAsset } = useWorkspaceAsset(assetId);
  const { basePath } = useAssetRouting(assetId);

  const { routineWorkflows, workspaceAsset, isLoading } = useRoutineAlerts(
    rawWorkspaceAsset?.id ?? '',
    routineId,
    undefined,
    { enabled: !!rawWorkspaceAsset?.id }
  );

  const getStateWithTransition = (transition: SlideAction) => {
    const {
      slideLayoutInitialTransition: _dropInitialTransition,
      ...routeState
    } = toRouteStateObject(location.state);

    return {
      ...routeState,
      slideLayoutInitialTransition: transition,
    };
  };

  const handleAlertSelected = (selectedAlert: RoutineWorkflow) => {
    if (!selectedAlert?.id) {
      return;
    }

    const editAlertPath = `${basePath}/routines/${routineId}/alerts/${selectedAlert.id}/edit`;

    if (slideLayout) {
      slideLayout.prepareSlideTransition(SlideAction.NEXT);
      navigate(editAlertPath, {
        state: getStateWithTransition(SlideAction.NEXT),
      });
      return;
    }

    const routeState = toRouteStateObject(location.state);
    navigate(
      editAlertPath,
      Object.keys(routeState).length > 0 ? { state: routeState } : undefined
    );
  };

  return (
    <View style={styles.container}>
      {!workspaceAsset || isLoading ? (
        <View style={styles.loadingContainer}>
          <LoadingIndicator flexContainer />
        </View>
      ) : (
        <View style={styles.scrollViewContainer}>
          <ScrollView>
            {routineWorkflows?.map((item: RoutineWorkflow, index: number) => (
              <AlertSummary
                key={`${index}-${item.id}`}
                workflow={item}
                workspaceAsset={workspaceAsset}
                routineId={routineId}
                onAlertSelected={handleAlertSelected}
              />
            ))}
          </ScrollView>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    margin: tokens.spacing.xl,
  },
  scrollViewContainer: {
    flex: 1,
    margin: tokens.spacing.xl,
  },
});
